import {
  AfterViewInit,
  Directive,
  ElementRef,
  HostListener,
  Input,
  Output,
  EventEmitter,
  DoCheck,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { CurrencyFormatterService } from '../services/currency-formatter.service';

@Directive({
  selector: '[appCurrencyInput]',
})
export class CurrencyInputDirective implements OnInit, OnChanges, DoCheck, AfterViewInit {
  private readonly element: HTMLInputElement;
  private lastValidValue: string = '';

  @Input() decimals: number = 2;
  @Input() showSymbol: boolean = false;
  @Input() allowNegative: boolean = false;
  /** Replaces the tenant's symbol, for an amount priced in another currency. */
  @Input() symbol?: string;
  /** false drops the thousands separators, for a figure that is not an amount, such as a percentage. */
  @Input() grouping: boolean = true;

  /**
   * Opt-in: also format a value the form wrote programmatically (patchValue,
   * reset) and when showSymbol/grouping change, instead of waiting for the
   * user to click into the field and out again.
   */
  @Input() liveFormat: boolean = false;

  @Output() valueChange = new EventEmitter<number>();

  constructor(
    private elementRef: ElementRef,
    private currencyFormatter: CurrencyFormatterService
  ) {
    this.element = this.elementRef.nativeElement as HTMLInputElement;
  }

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    // Format initial value after the reactive form has written its value to the DOM.
    // ngOnInit fires before FormControlName.ngOnInit sets element.value, so we use
    // ngAfterViewInit which fires after all directives in the host view are initialised.
    if (this.element.value) {
      this.formatValue(this.element.value, true, true);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // A change of currency relabels an amount that is already on screen.
    const symbol = changes['symbol'];
    if (symbol && !symbol.firstChange && this.element.value) {
      this.formatValue(this.element.value, true, true);
    }
    const display = changes['showSymbol'] ?? changes['grouping'];
    if (this.liveFormat && display && !display.firstChange && this.element.value) {
      this.formatValue(this.element.value, false, true);
    }
  }

  ngDoCheck(): void {
    if (!this.liveFormat) return;
    if (document.activeElement === this.element) return;
    const current = this.element.value;
    if (current && current !== this.lastValidValue) {
      this.formatValue(current, false, true);
    }
  }

  private get activeSymbol(): string {
    return this.symbol || this.currencyFormatter.getCurrencySymbol();
  }

  /** formatNumber always groups thousands; take the separators out when told to */
  private withGrouping(text: string): string {
    if (this.grouping) return text;
    const thousandSeparator =
      this.currencyFormatter.getConfig().thousandSeparator;
    return text.split(thousandSeparator).join('');
  }

  /** formatCurrency always prefixes the tenant's symbol; swap in the override */
  private withSymbol(text: string): string {
    const tenantSymbol = this.currencyFormatter.getCurrencySymbol();
    return this.symbol && this.symbol !== tenantSymbol
      ? text.replace(tenantSymbol, this.symbol)
      : text;
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.formatValue(value);
  }

  @HostListener('blur', ['$event'])
  onBlur(event: Event): void {
    // Re-format on blur to ensure proper formatting with full decimal places
    const value = (event.target as HTMLInputElement).value;
    this.formatValue(value, true, true);
  }

  @HostListener('focus', ['$event'])
  onFocus(event: Event): void {
    let value = this.element.value;
    if (!value) return;

    // Remove currency symbol
    if (this.showSymbol) {
      const symbol = this.activeSymbol;
      if (value.startsWith(symbol)) {
        value = value.slice(symbol.length);
      }
    }

    // Strip trailing decimal zeros so cursor lands after the significant digits.
    // "5,000.50" → "5,000.5", "5,000.00" → "5,000"
    // This prevents the next typed digit from landing in the 3rd decimal place.
    const decimalSeparator = this.currencyFormatter.getConfig().decimalSeparator;
    if (value.includes(decimalSeparator)) {
      value = value.replace(new RegExp(`\\${decimalSeparator}?0+$`), '');
    }

    this.element.value = value;
  }

  private formatValue(value: string, emit: boolean = true, padDecimals: boolean = false): void {
    if (!value) {
      this.element.value = '';
      if (emit) this.valueChange.emit(0);
      return;
    }

    // Remove currency symbol if present
    const symbol = this.activeSymbol;
    let cleanValue = value.replace(symbol, '');

    // Allow only numbers, decimal separator, and negative sign if allowed
    const decimalSeparator =
      this.currencyFormatter.getConfig().decimalSeparator;
    const thousandSeparator =
      this.currencyFormatter.getConfig().thousandSeparator;

    // Create regex pattern
    let pattern = `[^0-9\\${decimalSeparator}`;
    if (this.allowNegative) {
      pattern += '-';
    }
    pattern += ']';

    cleanValue = cleanValue.replace(new RegExp(pattern, 'g'), '');

    if (!cleanValue) {
      this.element.value = '';
      this.valueChange.emit(0);
      return;
    }

    // Replace thousand separators to avoid parsing issues
    cleanValue = cleanValue.replace(
      new RegExp(`\\${thousandSeparator}`, 'g'),
      ''
    );

    // Convert to standard decimal format for parsing
    const standardDecimal = cleanValue.replace(
      new RegExp(`\\${decimalSeparator}`, 'g'),
      '.'
    );

    // Parse as number
    let numValue = parseFloat(standardDecimal);

    if (isNaN(numValue)) {
      numValue = 0;
    }

    // During active typing, show only the digits the user has typed after the decimal
    // (not padded to this.decimals). On blur or programmatic load, pad fully.
    let formattedValue: string;
    const hasDecimal = cleanValue.includes(decimalSeparator);
    const decimalIndex = cleanValue.indexOf(decimalSeparator);
    const typedDecimalDigits = hasDecimal ? cleanValue.length - decimalIndex - 1 : 0;
    const decimalPlaces = padDecimals
      ? (hasDecimal ? this.decimals : 0)
      : Math.min(typedDecimalDigits, this.decimals);

    /* formatCurrency writes zero as "-", meant for a table cell. In an input
       that left a stray dash ("-%"), and replaced the "0." of "0.5" before the
       5 could be typed - so zero, and a decimal still being typed after it,
       are written as they are. */
    if (numValue === 0) {
      const zero = hasDecimal ? cleanValue : '0';
      formattedValue = this.showSymbol ? `${this.activeSymbol}${zero}` : zero;
    } else if (this.showSymbol) {
      formattedValue = this.withSymbol(
        this.currencyFormatter.formatCurrency(numValue, true, decimalPlaces)
      );
      // Preserve the decimal separator when the user has just typed it (no digits after yet)
      if (!padDecimals && hasDecimal && typedDecimalDigits === 0) {
        formattedValue += decimalSeparator;
      }
    } else {
      formattedValue = this.withGrouping(
        this.currencyFormatter.formatNumber(numValue, decimalPlaces)
      );
      if (!padDecimals && hasDecimal && typedDecimalDigits === 0) {
        formattedValue += decimalSeparator;
      }
    }

    // Update input value
    this.element.value = formattedValue;
    this.lastValidValue = formattedValue;

    // Emit the numeric value
    if (emit) this.valueChange.emit(numValue);
  }

  /**
   * Get the current numeric value
   */
  getValue(): number {
    return this.currencyFormatter.parseCurrency(this.element.value);
  }

  /**
   * Set the value programmatically
   */
  setValue(value: number): void {
    // Zero as itself, not formatCurrency's "-" (see formatValue).
    if (Number(value) === 0) {
      this.element.value = this.showSymbol ? `${this.activeSymbol}0` : '0';
      return;
    }
    if (this.showSymbol) {
      this.element.value = this.withSymbol(
        this.currencyFormatter.formatCurrency(value, true, this.decimals)
      );
    } else {
      this.element.value = this.withGrouping(
        this.currencyFormatter.formatNumber(value, this.decimals)
      );
    }
  }

  /**
   * Clear the input
   */
  clear(): void {
    this.element.value = '';
    this.valueChange.emit(0);
  }
}
